import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PanelLeftOpen } from "lucide-react";
import { memoryApi } from "@/lib/api";
import { useSession } from "@/lib/useAuth";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Select } from "@/components/ui/Toggle";
import { formatTs } from "@/lib/format";
import { cn } from "@/lib/cn";
import { useSidebar } from "@/lib/sidebar";

const SINCE_OPTIONS = [
  { value: "1", label: "最近 1 天" },
  { value: "7", label: "最近 7 天" },
  { value: "30", label: "最近 30 天" },
  { value: "90", label: "最近 90 天" },
  { value: "365", label: "最近 365 天" },
];

const OP_LABEL: Record<string, { label: string; cls: string }> = {
  commit: { label: "写入", cls: "bg-emerald-100 text-emerald-700" },
  quarantine: { label: "隔离", cls: "bg-red-100 text-red-700" },
  consolidate: { label: "归档", cls: "bg-zinc-100 text-zinc-700" },
  reactivate: { label: "解封", cls: "bg-blue-100 text-blue-700" },
};

function opBadge(op: string): { label: string; cls: string } {
  return OP_LABEL[op] ?? { label: op, cls: "bg-zinc-100 text-zinc-700" };
}

export function AuditLogPage(): JSX.Element {
  const session = useSession();
  const sidebar = useSidebar();
  const [sinceDays, setSinceDays] = useState(7);
  const query = useQuery({
    queryKey: ["memory", "audit", session.user_id, sinceDays] as const,
    queryFn: () => memoryApi.listAudit(session.token, sinceDays),
  });

  const rows = query.data?.rows ?? [];

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-6xl space-y-5 px-6 py-6">
        <header>
          <div className="flex items-center gap-2 text-[13px] text-zinc-500">
            {sidebar.collapsed ? (
              <button
                type="button"
                onClick={sidebar.toggle}
                className="-ml-2 rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
                title="展开侧栏"
              >
                <PanelLeftOpen className="h-4 w-4" />
              </button>
            ) : null}
            <span>Liando</span>
            <span className="text-zinc-300">/</span>
            <span className="text-zinc-800">审计日志</span>
          </div>
          <p className="mt-1 text-[12px] text-zinc-400">
            记录所有对长记忆的写入 / 隔离 / 归档操作。 普通用户只看到自己 + 系统的行; 管理员可看到全部。
          </p>
        </header>

      <Card>
        <CardHeader
          title="筛选"
          action={
            <Select
              value={String(sinceDays)}
              onChange={(e) => setSinceDays(Number(e.target.value))}
              options={SINCE_OPTIONS}
            />
          }
        />
        <CardBody className="p-0">
          {query.isLoading ? (
            <p className="px-6 py-4 text-sm text-zinc-400">加载中…</p>
          ) : query.isError ? (
            <p className="px-6 py-4 text-sm text-red-600">审计日志加载失败。</p>
          ) : rows.length === 0 ? (
            <p className="px-6 py-4 text-sm text-zinc-400">该时间段内没有审计记录。</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
                  <tr>
                    <th className="px-4 py-2 font-medium">时间</th>
                    <th className="px-4 py-2 font-medium">操作</th>
                    <th className="px-4 py-2 font-medium">条目</th>
                    <th className="px-4 py-2 font-medium">kind</th>
                    <th className="px-4 py-2 font-medium">scope</th>
                    <th className="px-4 py-2 font-medium">操作人</th>
                    <th className="px-4 py-2 font-medium">决策</th>
                    <th className="px-4 py-2 font-medium">置信度</th>
                    <th className="px-4 py-2 font-medium">备注</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {rows.map((r, i) => {
                    const badge = opBadge(r.op);
                    return (
                      <tr key={`${r.ts}-${r.entry_id}-${i}`} className="hover:bg-zinc-50">
                        <td className="whitespace-nowrap px-4 py-2 text-xs text-zinc-500">
                          {formatTs(r.ts)}
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className={cn(
                              "rounded-full px-2 py-0.5 text-xs font-medium",
                              badge.cls,
                            )}
                          >
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-zinc-600">
                          {r.entry_id.slice(0, 12)}…
                        </td>
                        <td className="px-4 py-2 text-xs text-zinc-700">{r.kind}</td>
                        <td className="px-4 py-2 text-xs text-zinc-500">{r.scope}</td>
                        <td className="px-4 py-2 font-mono text-xs text-zinc-500">
                          {r.acting_user_id ? r.acting_user_id.slice(0, 8) + "…" : "system"}
                        </td>
                        <td className="px-4 py-2 text-xs text-zinc-600">{r.decided_by}</td>
                        <td className="px-4 py-2 text-xs text-zinc-500">
                          {r.confidence != null ? (r.confidence * 100).toFixed(0) + "%" : "—"}
                        </td>
                        <td className="px-4 py-2 text-xs text-zinc-600">{r.note ?? ""}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      </div>
    </div>
  );
}
